import React, { ChangeEvent, useCallback } from "react";
import { MiAutocomplete, MiInputSelectBox } from "@miplatform/components";
import { Box, Divider, Typography } from "@mui/material";

export type optionsType = { label: string; value: string };

export type Filters = {
  groupBy: string;
  sortBy: string;
  accountId: string;
  communityId: string;
};

export const groupByOptions = [
  { label: "Customer", value: "billToAccountId" },
  { label: "Invoice Date", value: "invoicedDate" },
];

export const sortByOptions = [
  { label: "Invoice Date", value: "invoicedDate" },
  { label: "Amount Due", value: "amountDue" },
  { label: "Original Amount", value: "totalAmount" },
];

type FilterComponentProps = {
  customerOptions: optionsType[];
  communityOptions: optionsType[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
};

export const FilterComponent: React.FC<FilterComponentProps> = ({
  customerOptions,
  communityOptions,
  filters,
  setFilters,
}) => {
  const handleGroupByChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        groupBy:
          event.target.value.toLowerCase() === "none" ? "" : event.target.value,
      }));
    },
    [setFilters]
  );

  const handleSortChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: event.target.value,
      }));
    },
    [setFilters]
  );

  const handleCustomerChange = useCallback(
    (_: any, value: optionsType | null) => {
      setFilters((prev) => ({
        ...prev,
        accountId: value?.value || "",
      }));
    },
    [setFilters]
  );

  const handleCommunityChange = useCallback(
    (_: any, value: optionsType | null) => {
      setFilters((prev) => ({
        ...prev,
        communityId: value?.value || "",
      }));
    },
    [setFilters]
  );

  return (
    <Box height="100%" flex={0.2}>
      <Typography variant="body2" fontWeight={600}>
        Filter & Sort
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography fontSize={12} fontWeight={600} my={2}>
          Group By
        </Typography>
        <MiInputSelectBox
          options={groupByOptions}
          value={filters.groupBy}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handleGroupByChange(event)
          }
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Sort By
        </Typography>
        <MiInputSelectBox
          options={sortByOptions}
          value={filters.sortBy}
          disableNone
          handleChange={handleSortChange}
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Customer
        </Typography>
        <MiAutocomplete
          name="customerOptions"
          getOptionLabel={(option: optionsType) => option.label}
          options={customerOptions}
          value={
            customerOptions.find((opt) => opt.value === filters.accountId) ||
            null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          required
          onChange={handleCustomerChange}
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Community
        </Typography>
        <MiAutocomplete
          name="communityOptions"
          getOptionLabel={(option: optionsType) => option.label}
          options={communityOptions}
          value={
            communityOptions.find((opt) => opt.value === filters.communityId) ||
            null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          required
          onChange={handleCommunityChange}
        />
      </Box>
    </Box>
  );
};
